import { useCallback, useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import { getHeaders } from '../../utils/common'
import { DialogWrapper } from '../../components/general/DialogWrapper'
import Loading from '../../Loading'
import ErrorDisplay from "../../components/error/ErrorDisplay"

/**
 * @typedef {interface} DeleteFileParams
 * @prop {string} sessionID
 * @prop {string} target
 * @prop {boolean} isOpen
 * @prop {callback} onDelete
 * @prop {callback} closeModal
 * @prop {string} fileId
 */
interface DeleteFileParams {
  sessionID: string
  target: string
  isOpen: boolean
  onDelete: (name: string) => void
  closeModal: () => void
  fileId: string
}

/**
 * Delete file for session
 * @param {DeleteFileParams}
 * @returns {JSX.Element}
 */
export default function DeleteFile({ sessionID, target, onDelete, isOpen, closeModal, fileId }: DeleteFileParams) {
  const [deleteError, setDeleteError] = useState<Error | null>(null)
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false)
  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')

  useEffect(() => {
    setDeleteError(null)
    setIsDeleteInProgress(false)
  }, [isOpen])

  const submitDelete = useCallback(() => {
    setDeleteError(null)
    setIsDeleteInProgress(true)

    if (fileId === '') {
      setDeleteError(new Error(`file id not found`))
      return
    }

    fetch(`/mqttapi/v1/session/${sessionID}/file/${encodeURIComponent(target)}`, {
      method: 'DELETE',
      headers: myHeaders,
      body: JSON.stringify({
        id: fileId,
      }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.text()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then(() => {
        onDelete(target)
        closeModal()
      })
      .catch((error) => setDeleteError(error))
      .finally(() => setIsDeleteInProgress(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, sessionID, setIsDeleteInProgress, onDelete, closeModal])

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal} maxWidth="sm">
      <strong>
        Are you sure you want to delete <code>{target}</code>?
      </strong>
      {isDeleteInProgress ? (
        <Loading action="Deleting..." />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'right', gap: '20px' }}>
          <Button variant="contained" color="error" onClick={submitDelete}>
            Yes, DO IT
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      )}
      {deleteError !== null ? <ErrorDisplay err={deleteError} /> : null}
    </DialogWrapper>
  )
}
